/**
 * The per-answer finish for [NEEDS FOUNDER INPUT] tokens: gap-only sentences
 * gathered into one closing line, and a bare token left inside a sentence
 * named from the words around it. Shared by the draft finisher
 * (generators/questionData.ts), which stores the result, and the Draft view
 * (structuredAnswers.ts answerDisplay), which runs it again when it renders,
 * so a draft stored before the finish existed reads like a fresh one.
 *
 * Pure, no "use client" and no server-only imports: both sides load it.
 */

import type { ApplicationQuestion } from "@/constants/accelerators/questions/types";

export const BARE_PLACEHOLDER = "[NEEDS FOUNDER INPUT]";
/**
 * The token's opening as the model actually writes it: an underscore for
 * either space, any case. Every spelling is the length of
 * "[NEEDS FOUNDER INPUT", so slicing a match by that length finds its note.
 */
export const FOUNDER_INPUT_TOKEN_HEAD = String.raw`\[NEEDS[ _]FOUNDER[ _]INPUT`;
/**
 * The one token pattern, one level of nested `[...]` allowed so an embedded
 * markdown link inside a note survives intact. Shared by every consumer
 * (this file, the render-time humanizer in shared.ts, scoreAnonApp's count)
 * so they always agree on what counts as a token — a hand-rolled copy here
 * is how they'd quietly drift apart.
 */
export const PLACEHOLDER_TOKEN = new RegExp(`${FOUNDER_INPUT_TOKEN_HEAD}((?:[^[\\]]|\\[[^\\]]*\\])*)\\]`, "gi");
const TOKEN_HEAD_ANY_SPELLING = new RegExp(FOUNDER_INPUT_TOKEN_HEAD, "gi");

/** Every token in the canonical "[NEEDS FOUNDER INPUT" spelling, so exact-string checks (BARE_PLACEHOLDER) see it. */
export function canonicalFounderInputTokens(text: string): string {
  return text.replace(TOKEN_HEAD_ANY_SPELLING, "[NEEDS FOUNDER INPUT");
}
/** Stands in for a placeholder while a line is split into sentences, so a note's own full stop never splits it. */
const TOKEN_MARK = /\u0000(\d+)\u0000/g;
const LIST_PREFIX = /^(\s*(?:[-*+]|\d+\.)\s+)?/;
const SENTENCE_BREAK = /(?<=[.!?;])\s+/;
const CLAUSE_BREAK = /[,;]\s*|\s(?:and|but|or)\s/i;
const TRAILING_COPULA = /(?:\s(?:is|are|was|were)|:)$/i;
const MAX_NOTE_WORDS = 6;

function tokenMark(index: number): string {
  return `\u0000${index}\u0000`;
}

function leadPhrase(lead: string): string {
  const [first] = lead.split(/\s/);
  // An acronym ("MRR is ...") keeps its case; only a capitalised word is lowered.
  return first === first.toUpperCase() ? lead : lead.charAt(0).toLowerCase() + lead.slice(1);
}

/** The token's own note, or "" for a bare token. */
function placeholderNote(token: string): string {
  return token.slice("[NEEDS FOUNDER INPUT".length, -1).replace(/^\s*:\s*/, "").trim();
}

/** Names a gap that stays inside its sentence from the words around it: its own clause before it, else the words after it. */
function inlineNote(sentence: string, mark: string): string {
  const [before, after = ""] = sentence.split(mark).map((part) => part.replace(TOKEN_MARK, " "));
  const lead = (before.split(CLAUSE_BREAK).at(-1) ?? "").trim().replace(TRAILING_COPULA, "").trim();
  const words = lead
    ? lead.split(/\s+/).slice(-MAX_NOTE_WORDS)
    : (after.split(CLAUSE_BREAK)[0] ?? "").replace(/[.!?;:]+\s*$/, "").trim().split(/\s+/).slice(0, MAX_NOTE_WORDS);
  const phrase = words.join(" ");
  return phrase ? leadPhrase(phrase) : "";
}

/**
 * Replaces every token with `render(note)`, where `note` is the token's own
 * note with its ":" or dash separator dropped, or "" for a bare token.
 */
export function mapFounderInputTokens(text: string, render: (note: string) => string): string {
  return text.replace(PLACEHOLDER_TOKEN, (token) =>
    render(token.slice("[NEEDS FOUNDER INPUT".length, -1).replace(/^\s*[\u2014\u2013\-:]\s*/, "").trim()),
  );
}

/** A question's `missingNote` with the program named, or undefined when it has none. */
export function filledMissingNote(question: ApplicationQuestion, programName: string): string | undefined {
  return question.missingNote?.replaceAll("{programName}", programName);
}

/** "<short lead> is|are|was|were|: <placeholder>", and nothing else in the sentence. */
const GAP_SENTENCE = /^(\S+(?:\s+\S+){0,5}?)\s*(?:\s(?:is|are|was|were)\s|:\s*)\u0000(\d+)\u0000\s*[.;!]?$/i;

/** A lead holding a number or a second clause carries a fact ("Revenue is $8k and runway"); dropping it would lose that fact. */
const FACT_IN_LEAD = /\d|,|\b(?:is|are|was|were|and|but|or)\b/i;
/** "<clause> and|but|,|; <short lead> is|are|was|were|: <placeholder>", the placeholder ending the sentence. */
const TRAILING_GAP_CLAUSE =
  /^(.*\S)(?:\s*[,;]\s*|\s+(?:and|but)\s+)(\S+(?:\s+\S+){0,5}?)\s*(?:\s(?:is|are|was|were)\s|:\s*)\u0000(\d+)\u0000\s*[.;!]?$/i;
/** The kept clause must state something itself; "Revenue, burn" alone is a list of gaps, not a fact. */
const CLAUSE_WITH_FACT = /\d|\b(?:is|are|was|were)\b/i;

/**
 * Takes each short gap out of an answer's prose and appends one
 * "[NEEDS FOUNDER INPUT: <gap 1>; <gap 2>]" line at the end of that answer:
 * a whole "<short lead> is [NEEDS FOUNDER INPUT]." sentence goes, and a
 * trailing "and <short lead> is [NEEDS FOUNDER INPUT]" clause goes while the
 * fact before it stays. Any other bare placeholder left inside a sentence
 * gets a note naming it, so the render-time humanizer
 * (humanizeFounderInputPlaceholders) never drops its generic fallback
 * sentence mid-sentence. A placeholder already on its own line, or already
 * carrying a note, is left where it is. `sectionText` is one answer: the
 * lines under a "## " heading.
 */
export function consolidateInlinePlaceholders(sectionText: string): string {
  const gaps: string[] = [];
  let insertAt = -1;
  const kept: string[] = [];
  for (const line of sectionText.split("\n")) {
    const tokens: string[] = [];
    const marked = line.replace(PLACEHOLDER_TOKEN, (token) => tokenMark(tokens.push(token) - 1));
    const prefix = LIST_PREFIX.exec(marked)?.[0] ?? "";
    const body = marked.slice(prefix.length).trim();
    if (tokens.length === 0 || /^\u0000\d+\u0000$/.test(body)) {
      kept.push(line);
      continue;
    }
    const gapsBefore = gaps.length;
    let changed = false;
    const gather = (tokenIndex: string, lead: string) => {
      gaps.push(placeholderNote(tokens[Number(tokenIndex)]) || leadPhrase(lead));
      changed = true;
    };
    // Returns the text a sentence still needs on the line (its own gap(s)
    // moved to `gaps` already), or null when the whole sentence was a gap.
    // TRAILING_GAP_CLAUSE's kept clause can itself be a whole gap sentence
    // ("Revenue is [GAP] and runway is [GAP]." keeps "Revenue is [GAP]."),
    // so it recurses on that clause instead of pushing it verbatim — a
    // second call on the un-reprocessed clause would otherwise still find a
    // gap to consolidate, breaking the "a second call changes nothing" rule.
    const processSentence = (sentence: string): string | null => {
      const gap = GAP_SENTENCE.exec(sentence);
      if (gap && !FACT_IN_LEAD.test(gap[1])) {
        gather(gap[2], gap[1]);
        return null;
      }
      const tail = TRAILING_GAP_CLAUSE.exec(sentence);
      if (tail && CLAUSE_WITH_FACT.test(tail[1]) && !FACT_IN_LEAD.test(tail[2])) {
        gather(tail[3], tail[2]);
        return processSentence(`${tail[1]}.`);
      }
      return sentence.replace(TOKEN_MARK, (mark, i: string) => {
        const note = placeholderNote(tokens[Number(i)]) ? "" : inlineNote(sentence, mark);
        if (!note) return mark;
        changed = true;
        return `[NEEDS FOUNDER INPUT: ${note}]`;
      });
    };
    const remaining: string[] = [];
    for (const sentence of body.split(SENTENCE_BREAK)) {
      const processed = processSentence(sentence);
      if (processed !== null) remaining.push(processed);
    }
    if (!changed) {
      kept.push(line);
      continue;
    }
    if (insertAt === -1 && gaps.length > gapsBefore) insertAt = kept.length;
    if (remaining.length > 0) {
      kept.push(prefix + remaining.join(" ").replace(TOKEN_MARK, (_mark, i: string) => tokens[Number(i)]));
    }
  }
  if (gaps.length === 0) return kept.join("\n");
  const lastProse = kept.findLastIndex((line) => line.trim() !== "");
  kept.splice(lastProse === -1 ? insertAt : lastProse + 1, 0, `[NEEDS FOUNDER INPUT: ${[...new Set(gaps)].join("; ")}]`);
  return kept.join("\n");
}
